import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { useBidHistory } from '@/hooks/useBidHistory';
import { useBidHistoryStats } from '@/hooks/useBidHistoryStats';
import { BidHistoryKPICards } from '@/components/bid-history/BidHistoryKPICards';
import { BidHistoryFilters } from '@/components/bid-history/BidHistoryFilters';
import { BidHistoryTable } from '@/components/bid-history/BidHistoryTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, History } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { BidHistoryItem } from '@/types/bidHistory';

export default function BidHistoryPage() {
  const { user } = useAuth();
  const { bids, loading, error, filters, setFilters, refetch } = useBidHistory();
  const [allBidsForTrends, setAllBidsForTrends] = useState<BidHistoryItem[]>([]);

  // Fetch extended date range for trend calculations
  useEffect(() => {
    const fetchExtendedBids = async () => {
      if (!user?.id) return;

      try {
        // Get organization info from profiles table
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (!profile?.organization_id) return;

        // Get role via RPC function
        const { data: orgRole } = await supabase
          .rpc('get_user_org_role', {
            _user_id: user.id,
            _org_id: profile.organization_id
          } as any);

        // Get all vendor user IDs
        let vendorUserIds: string[] = [user.id];
        if (orgRole === 'owner' || orgRole === 'super_user') {
          const { data: orgMembers } = await supabase
            .from('profiles')
            .select('user_id')
            .eq('organization_id', profile.organization_id);
          
          if (orgMembers) {
            vendorUserIds = orgMembers.map(m => m.user_id);
          }
        }

        // Extended date range: go back twice the current filter period
        const periodDays = Math.ceil(
          (filters.dateRange.end.getTime() - filters.dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
        );
        const extendedStart = subDays(filters.dateRange.start, periodDays);

        const { data: extendedBidsData } = await supabase
          .from('bids')
          .select(`
            id,
            created_at,
            lead_time_days,
            vendor_user_id,
            rfq_id,
            rfqs!inner(
              id,
              public_id,
              created_at,
              closes_at,
              job_id,
              job:jobs(
                id,
                title,
                category,
                status,
                organization_id,
                organization:organizations(
                  id,
                  name
                )
              )
            )
          `)
          .in('vendor_user_id', vendorUserIds)
          .gte('created_at', startOfDay(extendedStart).toISOString())
          .lte('created_at', endOfDay(filters.dateRange.end).toISOString())
          .order('created_at', { ascending: false });

        if (extendedBidsData) {
          const jobIds = [...new Set(extendedBidsData.map(b => (b.rfqs as any)?.job?.id).filter(Boolean))];
          const bidIds = extendedBidsData.map(b => b.id);

          const [awardsResult, lineItemsResult] = await Promise.all([
            supabase.from('awards_vendor_view').select('id, job_id, winning_bid_id, awarded_at').in('job_id', jobIds),
            supabase.from('bid_line_items').select('bid_id, total_price_cents').in('bid_id', bidIds),
          ]);

          const awardsMap = new Map(awardsResult.data?.map(a => [a.job_id, a]) || []);
          const bidTotalsMap = new Map<string, number>();
          lineItemsResult.data?.forEach(item => {
            const current = bidTotalsMap.get(item.bid_id) || 0;
            bidTotalsMap.set(item.bid_id, current + item.total_price_cents);
          });

          const transformedBids: BidHistoryItem[] = extendedBidsData
            .filter(bid => bid.rfqs && (bid.rfqs as any).job)
            .map(bid => {
              const rfq = bid.rfqs as any;
              const job = rfq?.job;
              const org = job?.organization;
              const award = awardsMap.get(job?.id);

              let outcome: 'won' | 'lost' | 'pending' | 'expired' = 'pending';
              if (award) {
                outcome = award.winning_bid_id === bid.id ? 'won' : 'lost';
              } else if (job?.status === 'expired' || job?.status === 'cancelled') {
                outcome = 'expired';
              }

              const rfqCreatedAt = new Date(rfq.created_at);
              const bidCreatedAt = new Date(bid.created_at);
              const responseTimeHours = (bidCreatedAt.getTime() - rfqCreatedAt.getTime()) / (1000 * 60 * 60);

              return {
                id: bid.id,
                submittedAt: bid.created_at,
                jobId: job?.id || '',
                jobTitle: job?.title || 'Untitled Job',
                jobCategory: job?.category || 'other',
                rfqId: rfq?.id || '',
                rfqPublicId: rfq?.public_id || '',
                clientName: org?.name || 'Unknown Client',
                clientOrganizationId: job?.organization_id || '',
                totalBidCents: bidTotalsMap.get(bid.id) || 0,
                outcome,
                awardedAt: award?.awarded_at || null,
                awardAmountCents: null, // Award amount not available via restricted view
                selectionReasons: null,
                additionalComments: null,
                leadTimeDays: bid.lead_time_days,
                rfqClosesAt: rfq?.closes_at || '',
                responseTimeHours: Math.max(0, responseTimeHours),
              };
            });

          setAllBidsForTrends(transformedBids);
        }
      } catch (err) {
        console.error('Error fetching extended bids for trends:', err);
      }
    };

    fetchExtendedBids();
  }, [user?.id, filters.dateRange.start, filters.dateRange.end]);

  const stats = useBidHistoryStats({
    bids,
    allBids: allBidsForTrends,
    dateRange: filters.dateRange,
  });

  // Redirect non-vendors
  if (user?.role !== 'vendor') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <History className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Bid History</h1>
          <p className="text-muted-foreground">
            Track your bid performance and learn from outcomes
          </p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* KPI Cards */}
      <BidHistoryKPICards stats={stats} loading={loading} />

      {/* Filters */}
      <BidHistoryFilters filters={filters} onFiltersChange={setFilters} />

      {/* Table */}
      <BidHistoryTable bids={bids} loading={loading} />
    </div>
  );
}
